import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import {
  fetchStatus,
  clearStatusCache,
  type StatusBanner,
  type StatusService,
  type OutageInfo,
} from '../api/status';

const POLL_MS = 30_000;

interface StatusState {
  banners: StatusBanner[];
  services: StatusService[];
  outagePageActive: boolean;
  outageInfo: OutageInfo | null;
  publicHubStatusEnabled: boolean;
  loading: boolean;
  statusError: string | null;
  retryStatus: () => void;
}

const noopRetry = () => {};

const defaultState: StatusState = {
  banners: [],
  services: [],
  outagePageActive: false,
  outageInfo: null,
  publicHubStatusEnabled: false,
  loading: true,
  statusError: null,
  retryStatus: noopRetry,
};

const StatusContext = createContext<StatusState>(defaultState);

export const useStatus = () => useContext(StatusContext);

export const StatusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<Omit<StatusState, 'retryStatus'>>({
    banners: [],
    services: [],
    outagePageActive: false,
    outageInfo: null,
    publicHubStatusEnabled: false,
    loading: true,
    statusError: null,
  });
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(() => {
    fetchStatus()
      .then((s) => {
        // Treat missing `visible` as true (lite-admin / minimal push payloads omit it).
        const banners = (s.banners ?? [])
          .map((b, i) => ({
            ...b,
            visible: b.visible !== false,
            order: typeof b.order === 'number' ? b.order : i,
            severity:
              (b.severity as string) === 'warn'
                ? 'warning'
                : b.severity,
          }))
          .filter((b) => b.visible)
          .sort((a, b) => a.order - b.order);
        setState({
          banners,
          services: s.services ?? [],
          outagePageActive: s.outagePageActive ?? false,
          outageInfo: s.outageInfo ?? null,
          publicHubStatusEnabled: s.publicHubStatusEnabled ?? false,
          loading: false,
          statusError: null,
        });
      })
      .catch((err: unknown) => {
        const message =
          err instanceof Error ? err.message : 'Could not load status. Please try again.';
        setState((prev) => ({
          ...prev,
          loading: false,
          statusError: message,
        }));
      });
  }, []);

  const retryStatus = useCallback(() => {
    clearStatusCache();
    setState((prev) => ({ ...prev, loading: true, statusError: null }));
    load();
  }, [load]);

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, POLL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [load]);

  const value: StatusState = {
    ...state,
    retryStatus,
  };

  return <StatusContext.Provider value={value}>{children}</StatusContext.Provider>;
};
